import { LitElement, html, nothing } from 'lit';
import { currentLang } from '../i18n';
import { formatDeadlineLabel, isOverdue, subtaskDeadlineView } from '../helpers';
import { renderSourceIcon, resolveSourceIcon } from '../source-icons';
import { sanitizeHttpUrl } from '../url-sanitizer';
import type { GoalWithSubtasks, SubtaskWithResult } from '../types';
import { candidateConditions, candidateOriginView, candidateReasonView, noteText, rowTitle, type WorkItem } from '../work-list-view';
import {
  goalSourceLink,
  isRuntimeTask,
  taskConditions,
  taskDetailLead,
  taskDetailState,
  type TaskDetailState,
  type WorkDetailTarget,
} from '../work-detail-view';
import type { WorkListAction } from './work-list';
import { defineZenComponent } from './register';
import { isGoalToggling } from '../goals-actions';
import './goal-detail';

function sameTarget(a: WorkDetailTarget | null, b: WorkDetailTarget | null): boolean {
  if (!a || !b || a.type !== b.type) return a === b;
  if (a.type === 'candidate') return a.key === (b as typeof a).key;
  if (a.type === 'task') return a.subtaskId === (b as typeof a).subtaskId;
  return a.goalId === (b as typeof a).goalId;
}

/**
 * #2749: the detail beside the list. A task reads as 「ご確認いただきたい事項」
 * (the extension's #2748 board 6); a goal reads like the extension's goal
 * detail (board 7); a candidate shows what it is about and the two answers.
 */
export class ZenWorkDetail extends LitElement {
  static properties = {
    target: { attribute: false },
    goal: { attribute: false },
    item: { attribute: false },
    today: { attribute: false },
    loading: { attribute: false },
  };

  declare target: WorkDetailTarget | null;
  declare goal: GoalWithSubtasks | null;
  /** The list row it was opened from, while the list still has it. */
  declare item: WorkItem | null;
  declare today: string;
  /** Opened from a row whose goal has not been read yet. */
  declare loading: boolean;
  /** The goals ticked on the open held message, until it is recorded. */
  private linkChoice = { holdId: '', goalIds: new Set<string>() };

  constructor() {
    super();
    this.target = null;
    this.goal = null;
    this.item = null;
    this.today = '';
    this.loading = false;
  }

  createRenderRoot() {
    return this;
  }

  private onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.target) this.emit({ action: 'close-detail' });
  };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
    super.disconnectedCallback();
  }

  private get en() {
    return currentLang === 'en';
  }

  private emit(detail: WorkListAction) {
    this.dispatchEvent(new CustomEvent<WorkListAction>('work-list-action', { detail, bubbles: true, composed: true }));
  }

  updated(changed: Map<string, unknown>) {
    // A new target starts with an empty box; the same one keeps what is typed.
    const before = changed.get('target') as WorkDetailTarget | null | undefined;
    if (changed.has('target') && before && !sameTarget(before, this.target)) {
      this.querySelectorAll<HTMLTextAreaElement>('.task-detail-input').forEach((input) => { input.value = ''; });
    }
  }

  /** Moves focus to the heading, so a screen reader starts at the new detail. */
  focusHeading() {
    (this.querySelector('.work-detail-heading') as HTMLElement | null)?.focus();
  }

  private renderHeader(label: string, icon: string, goal: GoalWithSubtasks | null = null) {
    // A task opened from its goal goes back to the goal.
    const back = this.target?.type === 'task' && !!this.target.fromGoal;
    const close = back ? (this.en ? 'Back to the goal' : 'ゴールに戻る') : (this.en ? 'Close' : '閉じる');
    // #2932: a goal is deleted from its header, as in the extension.
    const remove = this.en ? 'Delete the goal' : 'ゴールを削除';
    return html`<div class="work-detail-header">
      <h2 class="work-detail-heading" tabindex="-1"><span class="mi mi-sm" aria-hidden="true">${icon}</span><span class="work-detail-heading-label">${label}</span></h2>
      ${goal ? html`<button type="button" class="work-detail-delete" aria-label=${remove} title=${remove}
        @click=${(e: Event) => this.dispatchEvent(new CustomEvent('goal-delete', { detail: { goalId: goal.id, title: goal.title, element: e.currentTarget }, bubbles: true, composed: true }))}>
        <span class="mi mi-sm" aria-hidden="true">delete</span>
      </button>` : nothing}
      <button type="button" class="work-detail-close" aria-label=${close} title=${close} @click=${() => this.emit({ action: 'close-detail' })}>
        <span class="mi mi-sm" aria-hidden="true">${back ? 'arrow_back' : 'close'}</span>
      </button>
    </div>`;
  }

  private task(): SubtaskWithResult | null {
    if (this.target?.type !== 'task' || !this.goal) return null;
    const id = this.target.subtaskId;
    return [...(this.goal.subtasks || []), ...(this.goal.decisions || [])].find((st) => st.id === id) ?? null;
  }

  private renderWho(st: SubtaskWithResult) {
    const ai = st.executor === 'ai';
    const label = ai ? 'AI' : (this.en ? 'You' : 'あなた');
    const inner = html`<span class="mi mi-xs" aria-hidden="true">${ai ? 'smart_toy' : 'person'}</span>${label}`;
    if (st.status === 'pending' && !isRuntimeTask(st)) {
      const action = ai ? (this.en ? 'Do it myself' : '自分でやる') : (this.en ? 'Hand to AI' : 'AIに任せる');
      return html`<button type="button" class=${`task-detail-who task-detail-who-${ai ? 'ai' : 'you'}`} title=${action} aria-label=${`${label}: ${action}`}
        @click=${() => this.emit({ action: 'executor', subtaskId: st.id, executor: ai ? 'human' : 'ai' })}>${inner}</button>`;
    }
    return html`<span class=${`task-detail-who task-detail-who-${ai ? 'ai' : 'you'}`}>${inner}</span>`;
  }

  private renderDeadline(st: SubtaskWithResult) {
    const shown = subtaskDeadlineView(st, this.goal?.deadline);
    const open = st.status !== 'done' && st.status !== 'invalidated';
    const editable = open && !isRuntimeTask(st);
    const edit = (event: Event) => this.emit({
      action: 'task-deadline',
      subtaskId: st.id,
      element: event.currentTarget as HTMLElement,
      current: shown.deadline ? shown.deadline.slice(0, 10) : null,
    });
    if (!shown.deadline) {
      return editable
        ? html`<button type="button" class="task-detail-deadline" @click=${edit}><span class="mi mi-xs" aria-hidden="true">event</span>${this.en ? 'No deadline' : '期限なし'}</button>`
        : nothing;
    }
    const overdue = open && isOverdue(shown.deadline);
    const inner = html`<span class="mi mi-xs" aria-hidden="true">${overdue ? 'error' : 'event'}</span>${formatDeadlineLabel(shown.deadline)}`;
    const cls = `task-detail-deadline${overdue ? ' overdue' : ''}`;
    return editable
      ? html`<button type="button" class=${cls} @click=${edit}>${inner}</button>`
      : html`<span class=${cls}>${inner}</span>`;
  }

  private renderResult(st: SubtaskWithResult, state: TaskDetailState) {
    if (state !== 'result' && state !== 'dispatch') return nothing;
    const readonly = state === 'dispatch' || isRuntimeTask(st);
    const copy = this.en ? "Copy the AI's result" : 'AIの結果をコピー';
    const onCopy = () => {
      const text = (this.querySelector('.task-detail-result') as HTMLTextAreaElement | null)?.value ?? '';
      void navigator.clipboard?.writeText(text);
    };
    return html`<div class="task-detail-card">
      <div class="task-detail-card-head"><span class="mi mi-sm" aria-hidden="true">edit_note</span><span>${this.en ? 'What the AI prepared' : 'AIが用意した内容'}</span>
        <button type="button" class="task-detail-copy" title=${copy} aria-label=${copy} @click=${onCopy}><span class="mi mi-xs" aria-hidden="true">content_copy</span></button>
      </div>
      <textarea class="task-detail-result" aria-label=${this.en ? "The AI's result" : 'AIの結果'} ?readonly=${readonly} .value=${st.ai_result?.result_content ?? ''}></textarea>
    </div>`;
  }

  private renderMemo(st: SubtaskWithResult) {
    if (!st.description) return nothing;
    return html`<div class="task-detail-section"><div class="task-detail-label">${this.en ? 'Memo' : 'メモ'}</div><div class="task-detail-memo">${st.description}</div></div>`;
  }

  private renderConditions(st: SubtaskWithResult) {
    const items = taskConditions(st);
    if (items.length === 0) return nothing;
    const disabled = isRuntimeTask(st);
    return html`<div class="task-detail-section"><div class="task-detail-label">${this.en ? 'Done when' : '完了の条件'}</div>
      ${items.map((item) => html`<label class="task-detail-condition">
        <input type="checkbox" .checked=${item.done} ?disabled=${disabled}
          @change=${(e: Event) => this.emit({ action: 'task-condition', subtaskId: st.id, index: item.index, done: (e.target as HTMLInputElement).checked })}>
        <span class=${item.done ? 'task-detail-condition-done' : ''}>${item.text}</span>
      </label>`)}
    </div>`;
  }

  /** Where it came from: the goal's source, its event, and the goal itself. */
  private renderReferences(goal: GoalWithSubtasks) {
    const rows: unknown[] = [];
    const source = goalSourceLink(goal);
    const url = sanitizeHttpUrl(source.url);
    if (url) {
      const icon = resolveSourceIcon(url, source.kind);
      const label = icon.kind === 'web' || icon.kind === 'generic' ? (this.en ? 'Source' : '元の情報') : icon.label;
      rows.push(html`<a class="task-detail-ref" href=${url} target="_blank" rel="noopener noreferrer">${renderSourceIcon(url, source.kind, 'xs')}<span class="task-detail-ref-text">${label}</span><span class="mi mi-xs" aria-hidden="true">open_in_new</span></a>`);
    }
    const event = goal.linked_event;
    if (event?.start_at && event.title) {
      const date = new Date(event.start_at);
      const time = event.is_all_day ? '' : ` ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
      rows.push(html`<div class="task-detail-ref"><span class="mi mi-xs" aria-hidden="true">event</span><span class="task-detail-ref-text">${date.getMonth() + 1}/${date.getDate()}${time} ${event.title}</span></div>`);
    }
    rows.push(html`<button type="button" class="task-detail-ref" @click=${() => this.emit({ action: 'open-goal', goalId: goal.id })}>
      <span class="mi mi-xs" aria-hidden="true">flag</span><span class="task-detail-ref-text">${this.en ? `Goal: ${goal.title}` : `ゴール「${goal.title}」`}</span><span class="mi mi-xs" aria-hidden="true">chevron_right</span>
    </button>`);
    return html`<div class="task-detail-section"><div class="task-detail-label">${this.en ? 'For reference' : 'ご参考'}</div>${rows}</div>`;
  }

  /** Short corrections, and a box for anything else that always reaches the AI. */
  private renderFeedback(goal: GoalWithSubtasks, st: SubtaskWithResult, state: TaskDetailState) {
    if (state === 'invalidated') return nothing;
    const en = this.en;
    const chip = (icon: string, label: string, onClick: (event: Event) => void) =>
      html`<button type="button" class="task-detail-chip" @click=${onClick}><span class="mi mi-xs" aria-hidden="true">${icon}</span>${label}</button>`;
    const chips: unknown[] = [];
    if (state === 'yours') {
      chips.push(chip('done_all', en ? 'Already done' : 'もう済んだ', () => this.emit({ action: 'complete-task', subtaskId: st.id })));
      const shown = subtaskDeadlineView(st, null);
      chips.push(chip('event_busy', en ? 'Wrong deadline' : '期限が違う', (event) => this.emit({
        action: 'task-deadline', subtaskId: st.id, element: event.currentTarget as HTMLElement, current: shown.deadline ? shown.deadline.slice(0, 10) : null,
      })));
    }
    if ((state === 'ai' || state === 'result' || state === 'no-result') && st.status === 'pending') {
      chips.push(chip('person', en ? "I'll do it" : '自分でやる', () => this.emit({ action: 'executor', subtaskId: st.id, executor: 'human' })));
    }
    if (state !== 'done' && !isRuntimeTask(st)) {
      chips.push(chip('block', en ? 'Not needed' : 'この件は不要', () => this.emit({ action: 'not-needed', subtaskId: st.id, title: st.title })));
    }
    const redo = state === 'result' || state === 'no-result';
    const placeholder = redo
      ? (en ? 'What should change? (e.g. a little more casual)' : '直してほしい点をどうぞ（例：もう少しくだけた文面で）')
      : (en ? 'Anything to tell the AI? (e.g. next week is fine)' : 'ご指示があればどうぞ（例：来週でよい、先に田中さんに確認する）');
    const send = redo ? (en ? 'Ask to redo' : '直してもらう') : (en ? 'Send' : '伝える');
    const submit = () => {
      const input = this.querySelector('.task-detail-input') as HTMLTextAreaElement | null;
      const text = input?.value.trim() ?? '';
      if (!input || !text) {
        input?.focus();
        return;
      }
      this.emit({ action: 'feedback', goalId: goal.id, subtaskId: st.id, taskTitle: st.title, text, redo });
      input.value = '';
    };
    return html`<div class="task-detail-feedback">
      <div class="task-detail-feedback-lead">${en ? 'If I got anything wrong, let me know.' : '私の理解に違うところがあれば、お知らせください。'}</div>
      ${chips.length > 0 ? html`<div class="task-detail-chips">${chips}</div>` : nothing}
      <div class="task-detail-box">
        <textarea class="task-detail-input" rows="2" placeholder=${placeholder} aria-label=${placeholder}></textarea>
        <button type="button" class="task-detail-send" aria-label=${send} title=${send} @click=${submit}><span class="mi mi-sm" aria-hidden="true">arrow_upward</span></button>
      </div>
    </div>`;
  }

  /** The one action that moves the task on. */
  private renderFooter(st: SubtaskWithResult, state: TaskDetailState) {
    const en = this.en;
    const approve = () => {
      const textarea = this.querySelector('.task-detail-result') as HTMLTextAreaElement | null;
      this.emit({ action: 'approve', subtaskId: st.id, resultContent: textarea && !textarea.readOnly ? textarea.value : null });
    };
    const focusFeedback = () => (this.querySelector('.task-detail-input') as HTMLTextAreaElement | null)?.focus();
    if (state === 'result' && !isRuntimeTask(st)) {
      return html`<div class="task-detail-footer">
        <button type="button" class="task-detail-primary" @click=${approve}><span class="mi mi-sm" aria-hidden="true">send</span>${en ? 'Go ahead with this' : 'この内容で進めてください'}</button>
        <button type="button" class="task-detail-secondary" @click=${focusFeedback}><span class="mi mi-sm" aria-hidden="true">edit</span>${en ? 'Ask for changes' : '直してもらう'}</button>
      </div>`;
    }
    if (state === 'result') {
      return html`<div class="task-detail-footer"><button type="button" class="task-detail-primary" @click=${approve}><span class="mi mi-sm" aria-hidden="true">thumb_up</span>${en ? 'Approve' : '承認する'}</button></div>`;
    }
    if (state === 'yours' && !isRuntimeTask(st)) {
      return html`<div class="task-detail-footer"><button type="button" class="task-detail-primary" @click=${() => this.emit({ action: 'complete-task', subtaskId: st.id })}><span class="mi mi-sm" aria-hidden="true">check_circle</span>${en ? 'Mark this task done' : 'このタスクを完了にする'}</button></div>`;
    }
    if (state === 'done') {
      return html`<div class="task-detail-footer"><button type="button" class="task-detail-secondary" @click=${() => this.emit({ action: 'undo-step', subtaskId: st.id })}><span class="mi mi-sm" aria-hidden="true">undo</span>${en ? 'Mark not done' : '未完了に戻す'}</button></div>`;
    }
    return nothing;
  }

  private renderTask(goal: GoalWithSubtasks, st: SubtaskWithResult) {
    const state = taskDetailState(st);
    const redo = state === 'ai' && !!st.rejection_feedback;
    return html`
      ${this.renderHeader(this.en ? 'For your review' : 'ご確認いただきたい事項', 'task_alt')}
      <div class="work-detail-body task-detail">
        <div class="task-detail-head">
          <h3 class="task-detail-title">${st.title}</h3>
          <div class="task-detail-lead">${taskDetailLead(state, redo, this.en ? 'en' : 'ja', st.status === 'blocked')}</div>
          ${redo ? html`<div class="task-detail-note"><span class="mi mi-xs" aria-hidden="true">replay</span>${st.rejection_feedback}</div>` : nothing}
          <div class="task-detail-meta">${this.renderWho(st)}${this.renderDeadline(st)}</div>
        </div>
        ${this.renderResult(st, state)}
        ${this.renderMemo(st)}
        ${this.renderConditions(st)}
        ${this.renderReferences(goal)}
        ${this.renderFeedback(goal, st, state)}
      </div>
      ${this.renderFooter(st, state)}
    `;
  }

  /** Laid out like the extension's goal detail; completing it is the one action at the bottom. */
  private renderGoal(goal: GoalWithSubtasks) {
    const en = this.en;
    const done = goal.status === 'done';
    return html`
      ${this.renderHeader(en ? 'Goal' : 'ゴール', 'flag', goal)}
      <div class="work-detail-body task-detail goal-detail"><zen-goal-detail .goal=${goal}></zen-goal-detail></div>
      <div class="task-detail-footer">
        ${done
          ? html`<button type="button" class="task-detail-secondary" ?disabled=${isGoalToggling(goal.id)}
              @click=${() => this.dispatchEvent(new CustomEvent('goal-toggle-done', { detail: { goalId: goal.id, value: true }, bubbles: true, composed: true }))}>
              <span class="mi mi-sm" aria-hidden="true">undo</span>${en ? 'Mark not done' : '未完了に戻す'}</button>`
          : html`<button type="button" class="task-detail-primary" ?disabled=${isGoalToggling(goal.id)} @click=${() => this.emit({ action: 'check-goal', goalId: goal.id })}>
              <span class="mi mi-sm" aria-hidden="true">check_circle</span>${en ? 'Complete this goal' : 'このゴールを完了にする'}</button>`}
      </div>
    `;
  }

  /**
   * #2745 (owner 2026-10-29): a goal candidate shows why it is put to the
   * user, what the goal would achieve, and where it came from.
   */
  private renderCandidate(item: WorkItem) {
    const en = this.en;
    const lang = en ? 'en' : 'ja';
    const reason = candidateReasonView(item.note, lang);
    const origin = candidateOriginView(item.note, lang);
    const conditions = candidateConditions(item.note);
    const summary = conditions.length > 0 ? '' : noteText(item.note, lang);
    const url = sanitizeHttpUrl(item.source?.url);
    const id = item.candidate_id!;
    return html`
      ${this.renderHeader(en ? 'Goal candidate' : 'ゴールの候補', 'outlined_flag')}
      <div class="work-detail-body task-detail">
        <div class="task-detail-head">
          <h3 class="task-detail-title">${item.title}</h3>
          <div class="task-detail-lead">${en ? 'Should this become a goal?' : 'ゴールにしますか？'}</div>
        </div>
        ${origin ? html`<div class="task-detail-section"><div class="task-detail-label">${en ? 'What arrived' : '届いたもの'}</div>
          ${origin.from ? html`<div class="task-detail-condition"><span class="mi mi-sm" aria-hidden="true">person</span><span>${origin.from}</span></div>` : nothing}
          ${url ? html`<a class="task-detail-ref" href=${url} target="_blank" rel="noopener noreferrer">${renderSourceIcon(url, item.source?.type, 'xs')}<span class="task-detail-ref-text">${origin.subject || (en ? 'Open the message' : '元のメッセージを開く')}</span><span class="mi mi-xs" aria-hidden="true">open_in_new</span></a>`
            : origin.subject ? html`<div class="task-detail-condition"><span class="mi mi-sm" aria-hidden="true">subject</span><span>${origin.subject}</span></div>` : nothing}
          ${origin.channel ? html`<div class="task-detail-condition"><span class="mi mi-sm" aria-hidden="true">tag</span><span>${origin.channel}</span></div>` : nothing}
          ${origin.at ? html`<div class="task-detail-condition"><span class="mi mi-sm" aria-hidden="true">schedule</span><span>${origin.at}</span></div>` : nothing}
        </div>` : nothing}
        ${reason ? html`<div class="task-detail-section"><div class="task-detail-label">${en ? 'Why you are asked' : '確認をお願いする理由'}</div>
          <div class="work-candidate-reason"><span class="mi mi-sm" aria-hidden="true">${reason.icon}</span>${reason.label}</div>
          <div class="task-detail-memo">${reason.detail}</div>
        </div>` : nothing}
        ${conditions.length > 0 ? html`<div class="task-detail-section"><div class="task-detail-label">${en ? 'As a goal, it is done when' : 'ゴールにすると、達成の条件'}</div>
          ${conditions.map((what) => html`<div class="task-detail-condition"><span class="mi mi-sm" aria-hidden="true">radio_button_unchecked</span><span>${what}</span></div>`)}
        </div>` : nothing}
        ${summary ? html`<div class="task-detail-section"><div class="task-detail-label">${en ? 'What it is about' : '内容'}</div><div class="task-detail-memo">${summary}</div></div>` : nothing}
        ${url && !origin ? html`<div class="task-detail-section"><div class="task-detail-label">${en ? 'For reference' : 'ご参考'}</div>
          <a class="task-detail-ref" href=${url} target="_blank" rel="noopener noreferrer">${renderSourceIcon(url, item.source?.type, 'xs')}<span class="task-detail-ref-text">${en ? 'Source' : '元の情報'}</span><span class="mi mi-xs" aria-hidden="true">open_in_new</span></a>
        </div>` : nothing}
      </div>
      <div class="task-detail-footer">
        <button type="button" class="task-detail-primary" @click=${() => this.emit({ action: 'candidate', candidateId: id, accept: true })}><span class="mi mi-sm" aria-hidden="true">add_task</span>${en ? 'Make a goal' : 'ゴールにする'}</button>
        <button type="button" class="task-detail-secondary" @click=${() => this.emit({ action: 'candidate', candidateId: id, accept: false })}><span class="mi mi-sm" aria-hidden="true">block</span>${en ? 'Not needed' : '不要'}</button>
      </div>
    `;
  }

  /** #1929 S4: a held thread message: which goal it continues, a new goal, or not needed. */
  private renderGoalLink(item: WorkItem) {
    const en = this.en;
    const lang = en ? 'en' : 'ja';
    const link = item.goal_link!;
    const holdId = link.hold_id;
    if (this.linkChoice.holdId !== holdId) this.linkChoice = { holdId, goalIds: new Set() };
    const chosen = this.linkChoice.goalIds;
    const url = sanitizeHttpUrl(link.message_url);
    const note = noteText(item.note, lang);
    const slack = link.provider === 'slack';
    return html`
      ${this.renderHeader(en ? 'For your review' : 'ご確認いただきたい事項', 'alt_route')}
      <div class="work-detail-body task-detail">
        <div class="task-detail-head">
          <h3 class="task-detail-title">${rowTitle(item, lang)}</h3>
          ${note ? html`<div class="task-detail-lead">${note}</div>` : nothing}
        </div>
        ${url ? html`<div class="task-detail-section"><div class="task-detail-label">${en ? 'For reference' : 'ご参考'}</div>
          <a class="task-detail-ref" href=${url} target="_blank" rel="noopener noreferrer">${renderSourceIcon(url, link.provider, 'xs')}<span class="task-detail-ref-text">${slack ? (en ? 'Open in Slack' : 'Slackで開く') : (en ? 'Open the mail' : 'メールを開く')}</span><span class="mi mi-xs" aria-hidden="true">open_in_new</span></a>
        </div>` : nothing}
        ${link.candidate_goals.length > 0 ? html`<div class="task-detail-section">
          <div class="task-detail-label">${en ? 'Which goals does it continue? Pick all that apply.' : 'どのゴールの続きですか？当てはまるものをすべて選んでください。'}</div>
          <div class="work-link-choices">${link.candidate_goals.map((goal) => html`<label class="work-link-choice"><input type="checkbox" .checked=${chosen.has(goal.goal_id)}
            @change=${(e: Event) => { if ((e.target as HTMLInputElement).checked) chosen.add(goal.goal_id); else chosen.delete(goal.goal_id); this.requestUpdate(); }}><span>${goal.title}</span></label>`)}</div>
        </div>` : nothing}
      </div>
      <div class="task-detail-footer">
        ${link.candidate_goals.length > 0 ? html`<button type="button" class="task-detail-primary" ?disabled=${chosen.size === 0}
          @click=${() => this.emit({ action: 'goal-link', holdId, goalIds: [...chosen] })}><span class="mi mi-sm" aria-hidden="true">link</span>${en ? 'Add to the chosen goals' : '選んだゴールに記録する'}</button>` : nothing}
        <button type="button" class=${link.candidate_goals.length > 0 ? 'task-detail-secondary' : 'task-detail-primary'} @click=${() => this.emit({ action: 'goal-link-new', holdId })}><span class="mi mi-sm" aria-hidden="true">add_task</span>${en ? 'New goal' : '新しいゴールにする'}</button>
        <button type="button" class="task-detail-secondary" @click=${() => this.emit({ action: 'goal-link-dismiss', holdId })}><span class="mi mi-sm" aria-hidden="true">block</span>${en ? 'Not needed' : '不要'}</button>
      </div>
    `;
  }

  render() {
    const target = this.target;
    if (!target) return nothing;
    if (target.type === 'candidate') {
      if (this.item?.goal_link) return this.renderGoalLink(this.item);
      return this.item?.candidate_id ? this.renderCandidate(this.item) : nothing;
    }
    if (!this.goal) {
      if (!this.loading) return nothing;
      const title = this.item?.title ?? '';
      return html`${this.renderHeader(this.en ? 'Loading' : '読み込み中', 'hourglass_empty')}
        <div class="work-detail-loading" role="status">
          ${title ? html`<p class="work-detail-loading-title">${title}</p>` : nothing}
          <p class="work-detail-loading-note"><span class="spinner" aria-hidden="true"></span>${this.en ? 'Reading this goal. It opens as soon as it arrives.' : 'このゴールを読み込んでいます。届きしだい表示します。'}</p>
        </div>`;
    }
    const st = this.task();
    return st ? this.renderTask(this.goal, st) : this.renderGoal(this.goal);
  }
}

defineZenComponent('zen-work-detail', ZenWorkDetail);
